import { useState, useEffect } from "react";
import { vastuAPI } from "../utils/api";

const DIRECTIONS = [
  "North", "Northeast", "East", "Southeast",
  "South", "Southwest", "West", "Northwest"
];

const ROOM_ITEMS = [
  { id: "main_entrance", label: "Main Entrance", icon: "🚪", defaultDir: "East", desc: "Main entry point of positive energy & light" },
  { id: "kitchen", label: "Kitchen", icon: "🔥", defaultDir: "Southeast", desc: "Agni corner (Fire element) placement" },
  { id: "master_bedroom", label: "Master Bedroom", icon: "🛏️", defaultDir: "Southwest", desc: "Earth element for stability & rest" },
  { id: "pooja_room", label: "Pooja Room", icon: "🪔", defaultDir: "Northeast", desc: "Ishaan corner for spiritual growth" },
  { id: "toilet_bathroom", label: "Toilet / Bathroom", icon: "🚿", defaultDir: "Northwest", desc: "Elimination & air flow zone" },
  { id: "underground_water_tank", label: "Underground Sump / Boring", icon: "💧", defaultDir: "Northeast", desc: "Water element placement for prosperity" },
  { id: "overhead_tank", label: "Overhead Water Tank", icon: "🏗️", defaultDir: "Southwest", desc: "Heavy weight balancing on rooftop" },
];

export default function VastuPage({ onAskAI }) {
  const [orientations, setOrientations] = useState({
    main_entrance: "East",
    kitchen: "Southeast",
    master_bedroom: "Southwest",
    pooja_room: "Northeast",
    toilet_bathroom: "Northwest",
    underground_water_tank: "Northeast",
    overhead_tank: "Southwest",
  });

  const [auditResult, setAuditResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activePreset, setActivePreset] = useState("ideal");

  const runAudit = async (currentOrientations) => {
    setLoading(true);
    try {
      const res = await vastuAPI.auditVastu(currentOrientations);
      setAuditResult(res.data);
    } catch (err) {
      console.error("Vastu audit error:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    runAudit(orientations);
  }, []);

  const handleDirChange = (roomKey, dir) => {
    const updated = { ...orientations, [roomKey]: dir };
    setOrientations(updated);
    setActivePreset("custom");
    runAudit(updated);
  };

  const applyPreset = (type) => {
    setActivePreset(type);
    let preset = {};
    if (type === "ideal") {
      preset = {
        main_entrance: "North",
        kitchen: "Southeast",
        master_bedroom: "Southwest",
        pooja_room: "Northeast",
        toilet_bathroom: "Northwest",
        underground_water_tank: "Northeast",
        overhead_tank: "Southwest",
      };
    } else if (type === "typical") {
      preset = {
        main_entrance: "East",
        kitchen: "Northwest",
        master_bedroom: "South",
        pooja_room: "East",
        toilet_bathroom: "South",
        underground_water_tank: "North",
        overhead_tank: "West",
      };
    } else if (type === "dosha_sample") {
      preset = {
        main_entrance: "Southwest",
        kitchen: "Northeast",
        master_bedroom: "Northeast",
        pooja_room: "South",
        toilet_bathroom: "Northeast",
        underground_water_tank: "Southwest",
        overhead_tank: "Northeast",
      };
    }
    setOrientations(preset);
    runAudit(preset);
  };

  const getScoreColor = (score) => {
    if (score >= 80) return "#10B981"; // green
    if (score >= 60) return "#2563EB"; // blue
    if (score >= 45) return "#F59E0B"; // amber
    return "#EF4444"; // red
  };

  return (
    <div className="tool-page-container">
      <div className="page-header">
        <div className="header-badge">🧭 VASTU SHASTRA AUDITOR</div>
        <h1>Vastu Compliance &amp; Dosha Checker 🌿</h1>
        <p>Select compass directions for rooms and water tanks — get instant Vastu scores and non-destructive remedies.</p>
      </div>

      {/* Preset Quick Filters */}
      <div className="preset-bar">
        <span className="preset-label">⚡ Quick Templates:</span>
        <button
          className={`preset-btn ${activePreset === "ideal" ? "active" : ""}`}
          onClick={() => applyPreset("ideal")}
        >
          🌟 100% Shubh (Ideal Standard)
        </button>
        <button
          className={`preset-btn ${activePreset === "typical" ? "active" : ""}`}
          onClick={() => applyPreset("typical")}
        >
          🏡 Standard Indian Flat/Plot
        </button>
        <button
          className={`preset-btn ${activePreset === "dosha_sample" ? "active" : ""}`}
          onClick={() => applyPreset("dosha_sample")}
        >
          ⚠️ Test Dosha Remediation
        </button>
      </div>

      <div className="vastu-grid-layout">
        {/* Left Column: Room Orientation Selectors */}
        <div className="card vastu-selectors-card">
          <h2 className="card-title">📐 Select Room &amp; Feature Directions</h2>
          <p className="card-subtitle">Set compass direction for each room according to your floor plan:</p>

          <div className="room-selectors-list">
            {ROOM_ITEMS.map((item) => {
              const currentDir = orientations[item.id] || item.defaultDir;
              return (
                <div key={item.id} className="room-selector-row">
                  <div className="room-info">
                    <span className="room-icon">{item.icon}</span>
                    <div>
                      <div className="room-name">{item.label}</div>
                      <div className="room-desc">{item.desc}</div>
                    </div>
                  </div>
                  <div className="direction-select-wrapper">
                    <select
                      className="direction-dropdown"
                      value={currentDir}
                      onChange={(e) => handleDirChange(item.id, e.target.value)}
                    >
                      {DIRECTIONS.map((d) => (
                        <option key={d} value={d}>
                          {d}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="vastu-compass-reference">
            <div className="compass-header">
              <span>🧭 8-Direction Vastu Chakra Reference:</span>
            </div>
            <img src="/diagrams/vastu-layout.svg" alt="Vastu 8 Direction Layout" className="compass-img" />
          </div>
        </div>

        {/* Right Column: Score Meter & Detailed Audit */}
        <div className="vastu-result-column">
          {auditResult && (
            <>
              {/* Score Hero Card */}
              <div className="card score-hero-card">
                <div className="score-header">
                  <div>
                    <div className="score-kicker">Vastu Harmony Index</div>
                    <div className="score-grade" style={{ color: getScoreColor(auditResult.vastu_score) }}>
                      {auditResult.grade}
                    </div>
                  </div>
                  <div className="score-circle" style={{ borderColor: getScoreColor(auditResult.vastu_score) }}>
                    <span className="score-num" style={{ color: getScoreColor(auditResult.vastu_score) }}>
                      {auditResult.vastu_score}
                    </span>
                    <span className="score-denom">/100</span>
                  </div>
                </div>
                <p className="score-summary">{auditResult.summary}</p>

                {/* Progress bar */}
                <div className="progress-track">
                  <div
                    className="progress-fill"
                    style={{
                      width: `${auditResult.vastu_score}%`,
                      backgroundColor: getScoreColor(auditResult.vastu_score),
                    }}
                  />
                </div>
              </div>

              {/* Individual Element Breakdown */}
              <div className="card element-audit-card">
                <h3 className="card-title">🔍 Detailed Room-by-Room Evaluation</h3>
                <div className="audit-items-list">
                  {auditResult.evaluations.map((ev, idx) => (
                    <div key={idx} className={`audit-item-row ${ev.status_code}`}>
                      <div className="audit-item-header">
                        <div className="audit-item-name">
                          <strong>{ev.element}</strong> in <span className="dir-tag">{ev.direction}</span>
                        </div>
                        <span className={`status-pill ${ev.status_code}`}>
                          {ev.status} ({ev.score_earned}/{ev.max_score} pts)
                        </span>
                      </div>
                      <div className="audit-item-tip">{ev.tip}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Recommended Remedies Card */}
              {auditResult.recommended_remedies && auditResult.recommended_remedies.length > 0 && (
                <div className="card remedies-card">
                  <div className="remedies-header">
                    <span className="remedies-icon">🛡️</span>
                    <div>
                      <h3 className="remedies-title">Non-Destructive Vastu Remedies</h3>
                      <p className="remedies-subtitle">{auditResult.doshas_count} Vastu doshas detected. Reduce negative effects using these simple remedies:</p>
                    </div>
                  </div>

                  <div className="remedies-list">
                    {auditResult.recommended_remedies.map((rem, rIdx) => (
                      <div key={rIdx} className="remedy-box">
                        <div className="remedy-title">
                          ⚠️ {rem.element} in {rem.direction}:
                        </div>
                        <div className="remedy-desc">{rem.remedy}</div>
                      </div>
                    ))}
                  </div>

                  {onAskAI && (
                    <button
                      className="action-ask-ai-btn"
                      onClick={() => onAskAI(`My Vastu score is ${auditResult.vastu_score}/100 with doshas in ${auditResult.dosha_list.join(", ")}. Please provide a detailed architectural solution.`)}
                    >
                      💬 Ask NirmanAI Assistant For Custom Vastu Plan
                    </button>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
